<app-ui-page-header title="Usuários" subtitle="Membros, convites e auto-convite por domínio." />

<div class="mt-6 grid gap-6">
  <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
    <app-ui-section-header
      [label]="'Convites (' + pendingInvitations().length + ')'"
      help="Convide pessoas e acompanhe os convites pendentes."
    >
      <button
        sectionActions
        appButton
        type="button"
        variant="ghost"
        (click)="load()"
        [disabled]="loading()"
      >
        <ng-icon name="heroArrowPath" size="16" aria-hidden="true" />
        Atualizar
      </button>
    </app-ui-section-header>

    <form
      class="grid gap-3 lg:grid-cols-[minmax(0,1fr)_16rem_auto]"
      [formGroup]="inviteForm"
      (ngSubmit)="createInvitation()"
    >
      <label class="grid gap-1.5">
        <span appUiLabel>E-mail</span>
        <input
          appInput
          type="email"
          formControlName="email"
          autocomplete="email"
          placeholder="nome@empresa.com.br"
          [readonly]="!canInviteMembers()"
          [attr.title]="!canInviteMembers() ? invitePermissionHint : null"
        />
      </label>
      <div class="grid gap-1.5">
        <span appUiLabel id="invite-role-label">Função</span>
        <div
          appSelect
          aria-labelledby="invite-role-label"
          [ngpSelectValue]="inviteForm.controls.roleId.value"
          [ngpSelectDisabled]="!canInviteMembers()"
          (ngpSelectValueChange)="selectInviteRole($event)"
          [attr.title]="!canInviteMembers() ? invitePermissionHint : null"
        >
          <span>{{ roleLabel(inviteForm.controls.roleId.value) }}</span>
          <div *ngpSelectPortal appSelectDropdown>
            @for (role of assignableRoles(); track role.id) {
              <div appSelectOption [ngpSelectOptionValue]="role.id">{{ role.name }}</div>
            }
          </div>
        </div>
      </div>
      <div class="flex items-end">
        <button
          appButton
          type="submit"
          variant="primary"
          [disabled]="saving() || inviteForm.invalid || !canInviteMembers()"
          [attr.title]="!canInviteMembers() ? invitePermissionHint : null"
        >
          <ng-icon name="heroPlus" size="16" aria-hidden="true" />
          Convidar
        </button>
      </div>
    </form>

    <div appUiDataTableShell>
      <table appUiDataTable>
        <thead appUiDataTableHead>
          <tr>
            <th appUiDataTableHeaderCell class="w-[40%]">E-mail</th>
            <th appUiDataTableHeaderCell class="w-[22%]">Expiração</th>
            <th appUiDataTableHeaderCell class="w-[16%]">Status</th>
            <th appUiDataTableHeaderCell class="w-[22%] text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          @for (invitation of pendingInvitations(); track invitation.id) {
            <tr appUiDataTableRow>
              <td appUiDataTableCell>
                <p class="truncate font-semibold text-ink">{{ invitation.email }}</p>
              </td>
              <td appUiDataTableCell class="text-muted">
                {{ invitation.expiresAt | date: 'dd/MM/yyyy' }}
              </td>
              <td appUiDataTableCell>
                <span appBadge tone="warning" size="xs">Pendente</span>
              </td>
              <td appUiDataTableCell>
                <div appUiDataTableActions>
                  <button
                    appButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    (click)="resendInvitation(invitation.id)"
                    [disabled]="isInvitationSaving(invitation.id) || !canInviteMembers()"
                    [attr.title]="!canInviteMembers() ? invitePermissionHint : 'Reenviar convite'"
                    aria-label="Reenviar convite"
                  >
                    <ng-icon name="heroEnvelope" size="15" aria-hidden="true" />
                  </button>
                  <button
                    appButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    (click)="revokeInvitation(invitation.id)"
                    [disabled]="isInvitationSaving(invitation.id) || !canInviteMembers()"
                    [attr.title]="!canInviteMembers() ? invitePermissionHint : 'Revogar convite'"
                    aria-label="Excluir convite"
                  >
                    <ng-icon name="heroTrash" size="15" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td appUiDataTableEmptyCell colspan="4">
                <app-ui-empty-state
                  compact
                  title="Nenhum convite pendente"
                  description="Convites enviados e ainda não aceitos aparecerão aqui."
                />
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
    <app-ui-section-header
      [label]="'Usuários (' + visibleMemberships().length + ')'"
      help="Membros ativos ou suspensos, com funções e equipes editáveis em linha."
    />

    <div appUiDataTableShell>
      <table appUiDataTable>
        <thead appUiDataTableHead>
          <tr>
            <th appUiDataTableHeaderCell class="w-[26%]">Usuário</th>
            <th appUiDataTableHeaderCell class="w-[14%]">Status</th>
            <th appUiDataTableHeaderCell class="w-[20%]">Funções</th>
            <th appUiDataTableHeaderCell class="w-[20%]">Equipes</th>
            <th appUiDataTableHeaderCell class="w-[12%]">Entrada</th>
            <th appUiDataTableHeaderCell class="w-[8%] text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          @for (membership of visibleMemberships(); track membership.id) {
            <tr appUiDataTableRow>
              <td appUiDataTableCell>
                <p class="flex items-center gap-1.5 truncate font-semibold text-ink">
                  <span class="truncate">{{
                    membership.user?.displayName || membership.user?.primaryEmail || membership.id
                  }}</span>
                  @if (isCreatorMembership(membership)) {
                    <span appBadge tone="info" size="xs" [attr.title]="creatorProtectionHint">Criador</span>
                  }
                </p>
                @if (membership.user?.primaryEmail) {
                  <p appUiHint class="mt-1 truncate">
                    {{ membership.user?.primaryEmail }}
                  </p>
                }
                @if (isMembershipSaving(membership.id)) {
                  <p class="mt-1 text-xs font-semibold text-accent-strong">Salvando...</p>
                }
              </td>
              <td appUiDataTableCell>
                <div
                  appSelect
                  [ngpSelectValue]="membership.status"
                  [ngpSelectDisabled]="isMembershipSaving(membership.id) || !canUpdateMemberships() || isCreatorMembership(membership)"
                  (ngpSelectValueChange)="selectMembershipStatus(membership, $event)"
                  [attr.title]="
                    isCreatorMembership(membership)
                      ? creatorProtectionHint
                      : !canUpdateMemberships()
                        ? updateMembershipPermissionHint
                        : null
                  "
                  [attr.aria-label]="
                    'Status de ' +
                    (membership.user?.displayName || membership.user?.primaryEmail || 'usuário')
                  "
                >
                  <span class="min-w-0 truncate">{{
                    membershipStatusLabel(membership.status)
                  }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (status of membershipStatusOptions; track status.value) {
                      <div appSelectOption [ngpSelectOptionValue]="status.value">
                        {{ status.label }}
                      </div>
                    }
                  </div>
                </div>
              </td>
              <td appUiDataTableCell>
                <div
                  appSelect
                  [ngpSelectMultiple]="true"
                  [ngpSelectValue]="membershipRoleIds(membership.id)"
                  [ngpSelectDisabled]="isMembershipSaving(membership.id) || !canAssignRoles()"
                  (ngpSelectValueChange)="selectMembershipRoles(membership, $event)"
                  [attr.title]="!canAssignRoles() ? assignRolesPermissionHint : null"
                  [attr.aria-label]="
                    'Funções de ' +
                    (membership.user?.displayName || membership.user?.primaryEmail || 'usuário')
                  "
                >
                  <span class="min-w-0 truncate">{{
                    roleSelectionLabel(membershipRoleIds(membership.id))
                  }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (role of assignableRoles(); track role.id) {
                      <div appSelectOption [ngpSelectOptionValue]="role.id">
                        {{ role.name }}
                        @if (membershipRoleIds(membership.id).includes(role.id)) {
                          <ng-icon name="heroCheck" size="15" aria-hidden="true" />
                        }
                      </div>
                    }
                  </div>
                </div>
              </td>
              <td appUiDataTableCell>
                <div
                  appSelect
                  [ngpSelectMultiple]="true"
                  [ngpSelectValue]="membershipTeamIds(membership.id)"
                  [ngpSelectDisabled]="isMembershipSaving(membership.id) || !canManageTeamMembers()"
                  (ngpSelectValueChange)="selectMembershipTeams(membership, $event)"
                  [attr.title]="!canManageTeamMembers() ? teamMembersPermissionHint : null"
                  [attr.aria-label]="
                    'Equipes de ' +
                    (membership.user?.displayName || membership.user?.primaryEmail || 'usuário')
                  "
                >
                  <span class="min-w-0 truncate">{{
                    teamSelectionLabel(membershipTeamIds(membership.id))
                  }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (team of activeTeams(); track team.id) {
                      <div appSelectOption [ngpSelectOptionValue]="team.id">
                        {{ team.name }}
                        @if (membershipTeamIds(membership.id).includes(team.id)) {
                          <ng-icon name="heroCheck" size="15" aria-hidden="true" />
                        }
                      </div>
                    }
                  </div>
                </div>
              </td>
              <td appUiDataTableCell class="text-muted">
                @if (membership.joinedAt) {
                  <span class="block truncate">{{ membership.joinedAt | date: 'dd/MM/yyyy' }}</span>
                } @else {
                  -
                }
              </td>
              <td appUiDataTableCell>
                <div appUiDataTableActions>
                  <button
                    appButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    (click)="removeMembership(membership)"
                    [disabled]="isMembershipSaving(membership.id) || !canRemoveMemberships() || isCreatorMembership(membership)"
                    [attr.title]="
                      isCreatorMembership(membership)
                        ? creatorProtectionHint
                        : !canRemoveMemberships()
                          ? removeMembershipPermissionHint
                          : null
                    "
                    aria-label="Excluir usuário"
                  >
                    <ng-icon name="heroTrash" size="15" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td appUiDataTableEmptyCell colspan="6">
                <app-ui-empty-state
                  compact
                  title="Nenhum usuário encontrado"
                  description="Usuários ativos ou convidados aparecerão aqui conforme os filtros aplicados."
                />
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  @if (isCurrentUserCreator()) {
    <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
      <app-ui-section-header
        label="Transferir titularidade"
        help="Você é o criador deste negócio — só você pode passar essa titularidade adiante. Ação irreversível."
      />
      <form
        class="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]"
        [formGroup]="transferForm"
        (ngSubmit)="transferOwnership()"
      >
        <app-ui-form-field label="Novo titular">
          <app-ui-select-field
            formControlName="targetMembershipId"
            [options]="transferCandidates()"
            placeholder="Selecione um membro ativo"
          />
        </app-ui-form-field>
        <div class="flex items-end">
          <button
            appButton
            type="submit"
            variant="danger"
            [disabled]="transferSaving() || transferForm.invalid || !transferCandidates().length"
          >
            Transferir titularidade
          </button>
        </div>
      </form>
      @if (!transferCandidates().length) {
        <p appUiHint>Convide e ative outro membro para poder transferir a titularidade.</p>
      }
    </section>
  }

  <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
    <app-ui-section-header
      label="Auto-convite por domínio"
      help="Domínios verificados podem receber uma função padrão para entrada controlada de usuários."
    />

    <form
      class="grid gap-3 lg:grid-cols-[minmax(0,1fr)_16rem_auto]"
      [formGroup]="domainForm"
      (ngSubmit)="createDomain()"
    >
      <label class="grid gap-1.5">
        <span appUiLabel>Domínio</span>
        <input
          appInput
          formControlName="domain"
          placeholder="empresa.com.br"
          autocomplete="off"
          [readonly]="!canInviteMembers()"
          [attr.title]="!canInviteMembers() ? invitePermissionHint : null"
        />
      </label>
      <div class="grid gap-1.5">
        <span appUiLabel id="domain-role-label">Função padrão</span>
        <div
          appSelect
          aria-labelledby="domain-role-label"
          [ngpSelectValue]="domainForm.controls.roleId.value"
          [ngpSelectDisabled]="!canInviteMembers()"
          (ngpSelectValueChange)="selectDomainRole($event)"
          [attr.title]="!canInviteMembers() ? invitePermissionHint : null"
        >
          <span>{{ roleLabel(domainForm.controls.roleId.value) }}</span>
          <div *ngpSelectPortal appSelectDropdown>
            @for (role of assignableRoles(); track role.id) {
              <div appSelectOption [ngpSelectOptionValue]="role.id">{{ role.name }}</div>
            }
          </div>
        </div>
      </div>
      <div class="flex items-end">
        <button
          appButton
          type="submit"
          variant="primary"
          [disabled]="saving() || domainForm.invalid || !canInviteMembers()"
          [attr.title]="!canInviteMembers() ? invitePermissionHint : null"
        >
          <ng-icon name="heroPlus" size="16" aria-hidden="true" />
          Criar
        </button>
      </div>
    </form>

    <div appUiDataTableShell>
      <table appUiDataTable>
        <thead appUiDataTableHead>
          <tr>
            <th appUiDataTableHeaderCell class="w-[30%]">Domínio</th>
            <th appUiDataTableHeaderCell class="w-[22%]">Verificação</th>
            <th appUiDataTableHeaderCell class="w-[18%]">Auto-convite</th>
            <th appUiDataTableHeaderCell class="w-[30%] text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          @for (domain of visibleDomains(); track domain.id) {
            <tr appUiDataTableRow>
              <td appUiDataTableCell>
                <p class="truncate font-semibold text-ink">{{ domain.domain }}</p>
              </td>
              <td appUiDataTableCell class="text-muted">
                @if (domain.verifiedAt) {
                  {{ domain.verifiedAt | date: 'dd/MM/yyyy' }}
                } @else {
                  Pendente
                }
              </td>
              <td appUiDataTableCell>
                <span
                  appBadge
                  [tone]="domain.autoInviteEnabled ? 'success' : 'neutral'"
                  size="xs"
                >
                  {{ domain.autoInviteEnabled ? 'Ativo' : 'Inativo' }}
                </span>
              </td>
              <td appUiDataTableCell>
                <div appUiDataTableActions>
                  @if (domain.status !== 'verified') {
                    <button
                      appButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      (click)="verifyDomain(domain.id)"
                      [disabled]="isDomainSaving(domain.id) || !canInviteMembers()"
                      aria-label="Verificar domínio"
                      [attr.title]="
                        !canInviteMembers() ? invitePermissionHint : 'Verificar domínio'
                      "
                    >
                      <ng-icon name="heroCheck" size="15" aria-hidden="true" />
                    </button>
                  }
                  <button
                    appButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    (click)="toggleDomain(domain)"
                    [disabled]="isDomainSaving(domain.id) || !canInviteMembers()"
                    [attr.aria-label]="
                      domain.autoInviteEnabled ? 'Desativar auto-convite' : 'Ativar auto-convite'
                    "
                    [attr.title]="
                      !canInviteMembers()
                        ? invitePermissionHint
                        : domain.autoInviteEnabled
                          ? 'Desativar auto-convite'
                          : 'Ativar auto-convite'
                    "
                  >
                    <ng-icon
                      [name]="domain.autoInviteEnabled ? 'heroNoSymbol' : 'heroShieldCheck'"
                      size="15"
                      aria-hidden="true"
                    />
                  </button>
                  @if (domain.status !== 'disabled') {
                    <button
                      appButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      (click)="disableDomain(domain.id)"
                      [disabled]="isDomainSaving(domain.id) || !canInviteMembers()"
                      aria-label="Desabilitar domínio"
                      [attr.title]="
                        !canInviteMembers() ? invitePermissionHint : 'Desabilitar domínio'
                      "
                    >
                      <ng-icon name="heroTrash" size="15" aria-hidden="true" />
                    </button>
                  }
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td appUiDataTableEmptyCell colspan="4">
                <app-ui-empty-state
                  compact
                  title="Nenhum domínio configurado"
                  description="Domínios permitidos para convites automáticos aparecerão aqui."
                />
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>
</div>
